import { PHOTO_EDGE_PX, type PhotoLook } from '@qq/shared';

/**
 * Turning a camera frame into a character portrait, entirely on this device.
 *
 * Nothing here touches the network. The "looks" are plain canvas pixel maths -
 * there is no AI service, no upload for processing, and no third party involved
 * at any point. Re-encoding through a canvas is also what drops the camera's
 * EXIF, including any GPS tag it attached.
 */

/** Centre-crops to a square and scales to PHOTO_EDGE_PX. */
export function toSquareCanvas(source: CanvasImageSource, sourceW: number, sourceH: number) {
  const edge = Math.min(sourceW, sourceH);
  const sx = (sourceW - edge) / 2;
  const sy = (sourceH - edge) / 2;

  const canvas = document.createElement('canvas');
  canvas.width = PHOTO_EDGE_PX;
  canvas.height = PHOTO_EDGE_PX;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('This browser cannot process images.');
  ctx.drawImage(source, sx, sy, edge, edge, 0, 0, PHOTO_EDGE_PX, PHOTO_EDGE_PX);
  return canvas;
}

export function applyLook(canvas: HTMLCanvasElement, look: PhotoLook): HTMLCanvasElement {
  if (look === 'natural') return canvas;

  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;
  const image = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const d = image.data;

  for (let i = 0; i < d.length; i += 4) {
    let r = d[i] as number;
    let g = d[i + 1] as number;
    let b = d[i + 2] as number;

    switch (look) {
      case 'poster': {
        // Flatten to a handful of levels: a sticker, not a photograph.
        const step = 255 / 5;
        r = Math.round(Math.round(r / step) * step);
        g = Math.round(Math.round(g / step) * step);
        b = Math.round(Math.round(b / step) * step);
        break;
      }
      case 'comic': {
        const step = 255 / 3;
        r = Math.min(255, Math.round(Math.round(r / step) * step) + 12);
        g = Math.min(255, Math.round(Math.round(g / step) * step) + 12);
        b = Math.min(255, Math.round(Math.round(b / step) * step) + 12);
        break;
      }
      case 'sunny': {
        r = Math.min(255, r * 1.12 + 12);
        g = Math.min(255, g * 1.05 + 8);
        b = Math.min(255, b * 0.95);
        break;
      }
      case 'mono': {
        const grey = 0.299 * r + 0.587 * g + 0.114 * b;
        r = Math.min(255, grey * 0.85 + 26);
        g = Math.min(255, grey * 0.9 + 30);
        b = Math.min(255, grey * 1.05 + 40);
        break;
      }
    }

    d[i] = r;
    d[i + 1] = g;
    d[i + 2] = b;
  }

  ctx.putImageData(image, 0, 0);
  return canvas;
}

/** JPEG at a quality that keeps a 512px portrait well under the size ceiling. */
export function toDataUrl(canvas: HTMLCanvasElement): string {
  return canvas.toDataURL('image/jpeg', 0.85);
}

export async function fileToSquareCanvas(file: File): Promise<HTMLCanvasElement> {
  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);
    return toSquareCanvas(img, img.naturalWidth, img.naturalHeight);
  } finally {
    URL.revokeObjectURL(url);
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('That picture could not be opened.'));
    img.src = src;
  });
}

export function cameraSupported(): boolean {
  return typeof navigator !== 'undefined' && Boolean(navigator.mediaDevices?.getUserMedia);
}
